export function productImageViewer(images) {
    let observer;
    let wheelHandler;
    let viewport;
    let modal;
    let thumbnailGallery;

    return {
        images,
        selectedImage: 0,
        opened: false,
        zoom: 1,
        rotation: 0,
        panX: 0,
        panY: 0,
        imageWidth: 0,
        imageHeight: 0,
        viewportWidth: 0,
        viewportHeight: 0,
        pointers: {},
        failedImages: {},
        get currentImage() { return this.images[this.selectedImage]; },
        get imageStyle() {
            const sideways = this.rotation % 180 !== 0;
            const width = this.imageWidth || 1;
            const height = this.imageHeight || 1;
            const fit = Math.min(
                this.viewportWidth / (sideways ? height : width),
                this.viewportHeight / (sideways ? width : height),
                1,
            );

            return {
                width: `${width * fit}px`,
                height: `${height * fit}px`,
                transform: `translate(-50%, -50%) translate(${this.panX}px, ${this.panY}px) rotate(${this.rotation}deg) scale(${this.zoom})`,
            };
        },
        init() {
            // Flux's dialog owns an Alpine scope, so find its elements from the
            // gallery root rather than relying on refs across that scope.
            viewport = this.$el.querySelector('[x-ref="viewport"]');
            modal = this.$el.querySelector('dialog');
            thumbnailGallery = this.$el.querySelector('[x-ref="modalThumbnails"]');
            observer = new ResizeObserver(() => {
                this.viewportWidth = viewport.clientWidth;
                this.viewportHeight = viewport.clientHeight;
                this.panX = this.panY = 0;
            });
            observer.observe(viewport);
            wheelHandler = event => {
                event.preventDefault();
                this.setZoom(this.zoom * Math.exp(-event.deltaY * (event.deltaMode === 1 ? 16 : 1) * 0.002));
            };
            viewport.addEventListener('wheel', wheelHandler, { passive: false });
        },
        destroy() {
            observer?.disconnect();
            viewport.removeEventListener('wheel', wheelHandler);
            modal.close();
            this.pointers = {};
        },
        openViewer(index = 0) {
            this.select(index);
            this.reset();
            this.opened = true;
            modal.showModal();
            this.revealThumbnail();
        },
        closeViewer() {
            modal.close();
        },
        onClose() {
            this.opened = false;
            this.reset();
        },
        select(index) {
            index = (index + this.images.length) % this.images.length;
            if (index === this.selectedImage) return;
            this.selectedImage = index;
            this.imageWidth = this.imageHeight = 0;
            this.reset();
            this.revealThumbnail();
        },
        revealThumbnail() {
            this.$nextTick(() => {
                const thumbnail = thumbnailGallery.children[this.selectedImage];
                if (!thumbnail) return;
                const top = thumbnail.offsetTop;
                if (top < thumbnailGallery.scrollTop) thumbnailGallery.scrollTop = top;
                else if (top + thumbnail.offsetHeight > thumbnailGallery.scrollTop + thumbnailGallery.clientHeight) {
                    thumbnailGallery.scrollTop = top + thumbnail.offsetHeight - thumbnailGallery.clientHeight;
                }
            });
        },
        reset() {
            this.zoom = 1;
            this.rotation = 0;
            this.panX = this.panY = 0;
            this.pointers = {};
        },
        setZoom(value) {
            this.zoom = Math.max(1, Math.min(8, value));
            if (this.zoom === 1) this.panX = this.panY = 0;
        },
        rotate(degrees) {
            this.rotation = (this.rotation + degrees + 360) % 360;
            this.panX = this.panY = 0;
            this.pointers = {};
        },
        imageLoaded(event) {
            this.imageWidth = event.target.naturalWidth;
            this.imageHeight = event.target.naturalHeight;
        },
        imageFailed(event) {
            this.failedImages[this.selectedImage] = true;
            if (!event.target.src.endsWith('/assets/logo.png')) event.target.src = '/assets/logo.png';
        },
        keydown(event) {
            if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                event.preventDefault();
                this.select(this.selectedImage + (event.key === 'ArrowLeft' ? -1 : 1));
            }
        },
        pointerDown(event) {
            if (event.pointerType === 'mouse' && event.button !== 0) return;
            viewport.setPointerCapture(event.pointerId);
            this.pointers[event.pointerId] = { x: event.clientX, y: event.clientY };
        },
        pointerMove(event) {
            const previous = this.pointers[event.pointerId];
            if (!previous) return;
            const before = Object.values(this.pointers);
            this.pointers[event.pointerId] = { x: event.clientX, y: event.clientY };
            const after = Object.values(this.pointers);

            if (after.length === 2) {
                const distance = points => Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
                const oldDistance = distance(before);
                if (oldDistance > 0) this.setZoom(this.zoom * distance(after) / oldDistance);
            } else if (after.length === 1 && this.zoom > 1) {
                this.panX += event.clientX - previous.x;
                this.panY += event.clientY - previous.y;
            }
        },
        pointerUp(event) {
            delete this.pointers[event.pointerId];
        },
    };
}

const register = () => window.Alpine.data('nriProductImageViewer', productImageViewer);
if (window.Alpine) register();
else document.addEventListener('alpine:init', register, { once: true });
